import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import * as API from "@ekonsilio/api-livechat";
import set from "lodash/set";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { isMobile } from "react-device-detect";
import { useIntl } from "react-intl";

import { getBehaviorsSessionStorage } from "./session-storage";
import { useWebmessenger } from "./webmessenger";

const BehaviorsContext = createContext();

const webmessengerApi = new API.Messaging.Webmessenger();

function BehaviorsProvider({ children }) {
    const intl = useIntl();
    const timer = useRef(null);

    const {
        maximized,
        openWindow,
        hide,
        show,
        greeting,
        setGreeting,
        createNotification,
        closeNotification,
        conversation,
        integration,
        online,
    } = useWebmessenger();

    const [isInit, setIsInit] = useState(false);
    const [hideChat, setHideChat] = useState(false);
    /* create this state only for showChat behavior */
    const [showChat, setShowChat] = useState(false);
    const [enabled, setEnabled] = useState(true);
    const [behaviors, setBehaviors] = useState(null);

    const [data, setData] = useState({
        timeOnSite: getBehaviorsSessionStorage()
            ? DateTime.now()
                  .diff(
                      DateTime.fromISO(
                          getBehaviorsSessionStorage().website.date
                      )
                  )
                  .as("seconds")
            : 0,
        timeOnPage: 0,
    });

    const refresh = useCallback(async () => {
        const storage = getBehaviorsSessionStorage();
        setData((previousState) => ({
            ...previousState,
            timeOnSite: storage
                ? DateTime.now()
                      .diff(DateTime.fromISO(storage.website.date))
                      .as("seconds")
                : 0,
            timeOnPage: storage
                ? DateTime.now()
                      .diff(DateTime.fromISO(storage.page.date))
                      .as("seconds")
                : 0,
        }));
    }, [setData]);

    const exec = useCallback(
        async (behavior, action, storage) => {
            let sentAt;
            switch (action.type) {
                case "openWindow":
                    if (!hideChat && storage.hideChat === false) {
                        closeNotification();
                        openWindow();
                    }
                    break;
                case "hideChat":
                    if (!conversation && !showChat) {
                        hide();
                        setHideChat(true);

                        storage.hideChat = true;
                        sessionStorage.setItem(
                            "eK-behaviors",
                            JSON.stringify(storage)
                        );
                    }
                    break;
                case "showChat":
                    if (!conversation && hideChat) {
                        storage.hideChat = false;
                        show();
                        setShowChat(true);
                        setHideChat(false);
                    }
                    break;
                case "notification":
                    if (!hideChat && !maximized) {
                        createNotification({
                            title:
                                action.title ||
                                intl.formatMessage({
                                    id: "WEBMESSENGER.NOTIFICATION.NEW_MESSAGE",
                                    defaultMessage: "Nouveau message",
                                }),
                            text: action.text,
                            behavior: behavior._id,
                        });
                    }
                    break;
                case "greeting":
                    if (action.subType === "message")
                        sentAt =
                            storage.triggered.greeting?.message?.sentAt ||
                            DateTime.now();

                    setGreeting((prevMessage) => ({
                        ...prevMessage,
                        [action.subType]: {
                            text: action.text,
                            title: action.title,
                            sentAt,
                        },
                    }));
                    break;
                default:
                    break;
            }

            /**
             * Return the values to be updated in the store
             */
            return (!hideChat && !storage.hideChat) ||
                action.type === "hideChat" ||
                action.type === "showChat"
                ? {
                      ...(action.type === "greeting"
                          ? {
                                [`triggered.greeting.${behavior._id}`]: true,
                                ...(action.subType === "message" && {
                                    "triggered.greeting.message.behavior":
                                        behavior._id,
                                    "triggered.greeting.message.sent":
                                        storage.triggered.greeting?.message
                                            ?.sent,
                                    "triggered.greeting.message.sentAt": sentAt,
                                    "triggered.greeting.message.text":
                                        action.text,
                                }),
                            }
                          : {
                                [`triggered.${behavior._id}`]: true,
                            }),
                  }
                : {};
        },
        [
            openWindow,
            hide,
            show,
            createNotification,
            setGreeting,
            closeNotification,
            conversation,
            hideChat,
            showChat,
            maximized,
        ]
    );

    const evaluate = useCallback(
        async (forceBehaviors) => {
            const storage = getBehaviorsSessionStorage();

            /**
             * Boolean to check if storage has changed
             */
            let updateStorage = false;

            /**
             * Set the storage by property key (ex: `greeting.message`)
             * @param {Object} triggered
             */

            const updateTriggeredBehavior = (triggered) => {
                for (const key in triggered) {
                    if (triggered) {
                        set(storage, key, triggered[key]);
                    }
                }
                if (!updateStorage) updateStorage = true;
            };

            if (storage) {
                const behaviorsData = behaviors || forceBehaviors;

                const pageUrl = new URL(window.location.href);

                pageUrl.timeOnSite = data.timeOnSite;
                pageUrl.timeOnPage = data.timeOnPage;
                pageUrl.deviceType = isMobile ? "mobile" : "desktop";
                // Save original pathname in path value
                pageUrl.path = pageUrl.pathname;
                // Include query params to the pathname during evaluation
                pageUrl.pathname += pageUrl.search;
                // remove ? to query params during evaluation
                pageUrl.queryString = pageUrl.search.slice(1);

                if (behaviorsData) {
                    const { default: Vote } = await import("./vote");

                    const res = await Vote.evaluate(
                        behaviorsData,
                        pageUrl,
                        "conditions"
                    );

                    const behaviorsHideChat = behaviorsData.filter(
                        ({ actions }) =>
                            actions.find((action) => action.type === "hideChat")
                    );

                    const behaviorsShowChat = behaviorsData.filter(
                        ({ actions }) =>
                            actions.find((action) => action.type === "showChat")
                    );

                    const isEvaluatedHideChat =
                        behaviorsHideChat?.length &&
                        behaviorsHideChat.find(
                            (behaviorOpenWindow) => res[behaviorOpenWindow._id]
                        );

                    const isEvaluatedShowChat =
                        behaviorsShowChat?.length &&
                        behaviorsShowChat.find(
                            (behaviorOpenWindow) => res[behaviorOpenWindow._id]
                        );

                    for (const [id, value] of Object.entries(res)) {
                        const behavior = behaviorsData.find(
                            (behaviorData) => behaviorData._id === id
                        );

                        if (behavior.active && value) {
                            /**
                             * Iterate over the actions
                             * (Multiple actions case (example: greeting))
                             */
                            for (const action of behavior.actions) {
                                /**
                                 * Because of the fact that there are two types of greeting,
                                 * the greeting message is only triggered if there is not already a message
                                 * in the store
                                 */
                                if (
                                    action.type === "greeting" &&
                                    (action.subType === "header" ||
                                        !conversation) &&
                                    !storage.triggered.greeting[behavior._id]
                                ) {
                                    updateTriggeredBehavior(
                                        await exec(behavior, action, storage)
                                    );
                                } else if (
                                    action.type === "hideChat" &&
                                    !showChat
                                )
                                    updateTriggeredBehavior(
                                        await exec(behavior, action, storage)
                                    );
                                else if (action.type === "showChat")
                                    updateTriggeredBehavior(
                                        await exec(behavior, action, storage)
                                    );
                                else if (action.type === "notification") {
                                    // Purposely nested condition
                                    if (!conversation)
                                        if (
                                            !storage.triggered?.notification?.[
                                                behavior._id
                                            ]
                                        ) {
                                            await exec(
                                                behavior,
                                                action,
                                                storage
                                            );
                                        }
                                } else if (
                                    ((action.type === "openWindow" &&
                                        (!isEvaluatedHideChat ||
                                            isEvaluatedHideChat.active ===
                                                true) &&
                                        (!isEvaluatedShowChat ||
                                            isEvaluatedShowChat.active ===
                                                true)) ||
                                        (action.type !== "openWindow" &&
                                            action.type !== "greeting" &&
                                            action.type !== "hideChat" &&
                                            action.type !== "showChat" &&
                                            action.type !== "notification") ||
                                        (action.type === "notification" &&
                                            /**
                                             * Display all notification only if they are not not associated to a behavior greeting message
                                             * or only if it associated to a behavior greeting message and the behavior has been triggered
                                             * and there is no conversation
                                             */
                                            (!behavior.actions.find(
                                                ({ subType }) =>
                                                    subType === "message"
                                            ) ||
                                                (!conversation &&
                                                    storage.triggered.greeting
                                                        .message._id ===
                                                        behavior._id)))) &&
                                    !storage.triggered[behavior._id]
                                ) {
                                    updateTriggeredBehavior(
                                        await exec(behavior, action, storage)
                                    );
                                }
                            }
                        }
                    }
                }

                /**
                 * Set the greeting message if there is no greeting displayed and there is a message in the store
                 * change page case
                 */
                if (storage.triggered.greeting?.message && !greeting?.message) {
                    setGreeting((prevMessage) => ({
                        ...prevMessage,
                        message: {
                            text: storage.triggered.greeting.message.text,
                            sentAt: storage.triggered.greeting.message.sentAt,
                        },
                    }));
                }

                if (updateStorage)
                    sessionStorage.setItem(
                        "eK-behaviors",
                        JSON.stringify(storage)
                    );
            }
        },
        [behaviors, data, exec, setGreeting, conversation, greeting]
    );

    const disable = useCallback(() => {
        if (enabled) {
            setEnabled(false);
        }
    }, [enabled]);

    const enable = useCallback(() => {
        if (!enabled) {
            setEnabled(true);
        }
    }, [enabled]);

    const triggerOpenWindowBehaviors = useCallback(async () => {
        if (behaviors) {
            const storage = getBehaviorsSessionStorage();

            const openWindowBehaviors = behaviors
                .filter(
                    (behavior) =>
                        !!behavior.actions.find(
                            (action) => action.type === "openWindow"
                        )
                )
                .reduce(
                    (res, behavior) => ({
                        ...res,
                        [`triggered.${behavior._id}`]: true,
                    }),
                    {}
                );

            for (const key in openWindowBehaviors) {
                if (openWindowBehaviors[key])
                    set(storage, key, openWindowBehaviors[key]);
            }

            sessionStorage.setItem("eK-behaviors", JSON.stringify(storage));
        }
    }, [behaviors]);

    useEffect(() => {
        if (integration !== null && enabled) {
            const store = getBehaviorsSessionStorage();

            if (!store) {
                sessionStorage.setItem(
                    "eK-behaviors",
                    JSON.stringify({
                        website: {
                            date: DateTime.now(),
                        },
                        page: {
                            href: window.location.href,
                            date: DateTime.now(),
                        },
                        triggered: {
                            greeting: {},
                        },
                    })
                );
            }

            if (store) {
                sessionStorage.setItem(
                    "eK-behaviors",
                    JSON.stringify({
                        ...store,
                        ...(store.page.href !== window.location.href
                            ? {
                                  page: {
                                      href: window.location.href,
                                      date: DateTime.now(),
                                  },
                              }
                            : {}),
                        triggered: {
                            ...store.triggered,
                            /**
                             * Empty the previous greeting to reset it (greeting header)
                             */
                            greeting: {},
                        },
                    })
                );
            }

            webmessengerApi
                .getBehaviors(integration)
                .then(async (behaviorsElements) => {
                    setBehaviors(behaviorsElements);
                })
                .catch((err) => err);
        }
    }, [integration, enabled]);

    useEffect(() => {
        if (online && behaviors !== null && isInit === false) {
            refresh()
                .then(() => evaluate(behaviors))
                .then(() => setIsInit(true));
        }
    }, [online, behaviors, isInit]);

    useEffect(() => {
        if (enabled) {
            timer.current = setInterval(async () => {
                await refresh();
                await evaluate();
            }, 2000);
        }

        return () => {
            clearInterval(timer.current);
        };
    }, [behaviors, evaluate, refresh, enabled]);

    useEffect(() => {
        if (conversation !== null) {
            if (hideChat) {
                show();
                setHideChat(false);
            }
            disable();
        }
    }, [conversation, disable, hideChat, show]);

    useEffect(() => {
        if (isInit) {
            const storage = getBehaviorsSessionStorage();
            storage.hideChat = hideChat;
            sessionStorage.setItem("eK-behaviors", JSON.stringify(storage));
        }
    }, [hideChat, isInit]);

    const values = useMemo(
        () => ({
            behaviors,
            disable,
            enable,
            hideChat,
            triggerOpenWindowBehaviors,
            isInit,
        }),
        [behaviors, disable, enable, hideChat, isInit]
    );
    return (
        <BehaviorsContext.Provider value={values}>
            {children}
        </BehaviorsContext.Provider>
    );
}

function useBehaviors() {
    const context = useContext(BehaviorsContext);
    if (context === undefined) {
        throw new Error("useBehaviors must be used within a BehaviorsProvider");
    }
    return context;
}

BehaviorsProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export { BehaviorsProvider, useBehaviors };
